import { Head, Link } from '@inertiajs/react'
import { Crown, Check, X, DollarSign, Users } from 'lucide-react'
import MainLayout from '@/components/Layout/MainLayout'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { godModePlans } from '@/routeHelpers'
import type { CurrentUser } from '@/types/shared'

interface Entitlement {
  id: string
  key: string
  name: string
  value: number | boolean
  value_type: string
}

interface Price {
  id: string
  interval: string
  amount_cents: number
  currency: string
  stripe_price_id: string | null
}

interface Plan {
  id: string
  name: string
  slug: string
  description: string
  internal: boolean
  active: boolean
  position: number
  subscriptions_count: number
  entitlements: Entitlement[]
  prices: Price[]
}

interface PlansProps {
  current_user: CurrentUser
  god_mode: boolean
  plans: Plan[]
  entitlements: Entitlement[]
}

function formatValue(value: number | boolean, valueType: string): string {
  if (valueType === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  if (value === -1) {
    return 'Unlimited'
  }
  return String(value)
}

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

export default function Plans({ current_user, god_mode, plans }: PlansProps) {
  return (
    <>
      <Head title="Plans - God Mode" />
      <MainLayout
        user={current_user}
        breadcrumbs={[
          { label: 'God Mode' },
          { label: 'Plans' },
        ]}
        godMode={god_mode}
      >
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <Crown className="size-6 text-amber-500" />
                <h1 className="text-2xl font-semibold">Plans</h1>
              </div>
              <p className="text-muted-foreground">
                Manage subscription plans and their entitlements.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {plans.map((plan) => (
                <Link
                  key={plan.id}
                  href={godModePlans.show.path({ id: plan.id })}
                  className="block cursor-pointer"
                >
                  <Card className="h-full hover:border-primary/50 transition-colors">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          {plan.name}
                          {plan.internal && (
                            <Badge variant="secondary" className="text-xs">Internal</Badge>
                          )}
                        </CardTitle>
                        {!plan.active && (
                          <Badge variant="outline" className="text-xs text-muted-foreground">Inactive</Badge>
                        )}
                      </div>
                      <CardDescription>{plan.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1.5">
                          <Users className="size-4 text-muted-foreground" />
                          <span>{plan.subscriptions_count} subscriptions</span>
                        </div>
                      </div>

                      {plan.prices.length > 0 && (
                        <div className="flex gap-4">
                          {plan.prices.map((price) => (
                            <div key={price.id} className="flex items-center gap-1 text-sm">
                              <DollarSign className="size-3 text-muted-foreground" />
                              <span className="font-medium">{formatPrice(price.amount_cents)}</span>
                              <span className="text-muted-foreground">/{price.interval === 'monthly' ? 'mo' : 'yr'}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="border-t pt-4">
                        <p className="text-xs text-muted-foreground mb-2">Entitlements</p>
                        <div className="space-y-1.5">
                          {plan.entitlements.slice(0, 4).map((ent) => (
                            <div key={ent.id} className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">{ent.name}</span>
                              <span className="flex items-center gap-1">
                                {ent.value_type === 'boolean' ? (
                                  ent.value ? (
                                    <Check className="size-4 text-green-500" />
                                  ) : (
                                    <X className="size-4 text-red-500" />
                                  )
                                ) : (
                                  <span className="font-medium">{formatValue(ent.value, ent.value_type)}</span>
                                )}
                              </span>
                            </div>
                          ))}
                          {plan.entitlements.length > 4 && (
                            <p className="text-xs text-muted-foreground">+{plan.entitlements.length - 4} more</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </MainLayout>
    </>
  )
}
